/**
 * Three.js Scene Utilities
 * 
 * Shared utility functions for Three.js scene components to eliminate
 * code duplication and maintain consistency across game types.
 */

import * as THREE from 'three';
import { calculateHTMLSize } from './three-html-scale';
import { EMPIRICAL_CORRECTIONS, PIXEL_TO_UNIT } from '../constants/three-scene.constants';

/**
 * Calculate HTML dimensions in 3D units with empirical correction
 * 
 * This function encapsulates the common pattern used across all scene components
 * for converting pixel dimensions to accurate 3D units.
 * 
 * @param pixelWidth - HTML content width in pixels
 * @param pixelHeight - HTML content height in pixels (defaults to width for square content)
 * @param store - Angular Three store instance
 * @returns Object with width and height in 3D units (includes 1% correction)
 * 
 * @example
 * ```typescript
 * protected htmlDimensions3D = computed(() => {
 *   return calculateHTMLDimensions3D(
 *     1000, // width in pixels
 *     1000, // height in pixels
 *     this.store.get()
 *   );
 * });
 * ```
 */
export function calculateHTMLDimensions3D(
  pixelWidth: number,
  pixelHeight: number = pixelWidth,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  store: any // Angular Three store state - complex internal type
): { width: number; height: number } {
  const camera = store.camera;
  const size = store.size;
  
  // Fallback when camera not ready yet - use approximation formula
  if (!camera || !size) {
    const { CALIBRATION_DIVISOR, BASE_MULTIPLIER, DIAMETER_FACTOR } = PIXEL_TO_UNIT;
    const width = (pixelWidth / CALIBRATION_DIVISOR) * BASE_MULTIPLIER * DIAMETER_FACTOR;
    const height = (pixelHeight / CALIBRATION_DIVISOR) * BASE_MULTIPLIER * DIAMETER_FACTOR;
    
    // Apply empirical correction to fallback too
    return {
      width: width * EMPIRICAL_CORRECTIONS.HTML_SIZE_MULTIPLIER,
      height: height * EMPIRICAL_CORRECTIONS.HTML_SIZE_MULTIPLIER,
    };
  }
  
  // Use accurate pixel-to-unit conversion utility
  const position = new THREE.Vector3(0, 0, 0);
  const distanceFactor = 0; // Pixel-perfect mode
  
  const [width] = calculateHTMLSize(
    pixelWidth,
    pixelWidth, // Pass same for consistency
    position,
    camera,
    { width: size.width, height: size.height },
    distanceFactor
  );
  
  const [, height] = calculateHTMLSize(
    pixelHeight,
    pixelHeight,
    position,
    camera,
    { width: size.width, height: size.height },
    distanceFactor
  );
  
  // Apply empirical correction to match actual HTML visual extent
  return {
    width: width * EMPIRICAL_CORRECTIONS.HTML_SIZE_MULTIPLIER,
    height: height * EMPIRICAL_CORRECTIONS.HTML_SIZE_MULTIPLIER,
  };
}
